<template>
    <section aria-labelledby="filter-heading">
        <h2 id="filter-heading" class="sr-only">Filters</h2>

        <div class="relative z-10 bg-white border-b border-gray-200 pb-4">
            <div class="max-w-7xl mx-auto px-4 flex items-center justify-between sm:px-6 lg:px-8">
                <Menu as="div" class="relative inline-block text-left">
                    <div>
                        <MenuButton class="group inline-flex justify-center text-sm font-medium text-gray-700 hover:text-gray-900">
                            {{ getActiveSortName }}
                            <ChevronDownIcon class="flex-shrink-0 -mr-1 ml-1 h-5 w-5 text-gray-400 group-hover:text-gray-500" aria-hidden="true"/>
                        </MenuButton>
                    </div>

                    <transition enter-active-class="transition ease-out duration-100"
                        enter-from-class="transform opacity-0 scale-95"
                        enter-to-class="transform opacity-100 scale-100"
                        leave-active-class="transition ease-in duration-75"
                        leave-from-class="transform opacity-100 scale-100"
                        leave-to-class="transform opacity-0 scale-95">
                        <MenuItems class="origin-top-left absolute left-0 mt-2 w-40 rounded-md shadow-2xl bg-white ring-1 ring-black ring-opacity-5 focus:outline-none">
                            <div class="py-1">
                                <MenuItem v-for="option in sortOptions" :key="option.name" v-slot="{ active }">
                                    <a @click="_ => this.handleSort(option.value)"
                                       :class="[option.current ? 'font-medium text-gray-900' : 'text-gray-500', active ? 'bg-gray-100' : '', 'cursor-pointer block px-4 py-2 text-sm']">
                                        {{ option.name }}
                                    </a>
                                </MenuItem>
                            </div>
                        </MenuItems>
                    </transition>
                </Menu>

                <button type="button" class="inline-block text-sm font-medium text-gray-700 hover:text-gray-900 sm:hidden" @click="this.showMobileFilters()">
                    Filters
                </button>

                <div class="hidden sm:block">
                    <div class="flow-root">
                        <PopoverGroup class="-mx-4 flex items-center divide-x divide-gray-200">
                            <Popover v-for="section in filters" :key="section.name" class="px-4 relative inline-block text-left">
                                <PopoverButton class="group inline-flex justify-center text-sm font-medium text-gray-700 hover:text-gray-900">
                                    <span>{{ section.name }}</span>
                                    <span v-if="getActiveCount(section)" class="ml-1.5 rounded py-0.5 px-1.5 bg-gray-200 text-xs font-semibold text-gray-700 tabular-nums">
                                        {{ getActiveCount(section) }}
                                    </span>
                                    <ChevronDownIcon class="flex-shrink-0 -mr-1 ml-1 h-5 w-5 text-gray-400 group-hover:text-gray-500" aria-hidden="true"/>
                                </PopoverButton>

                                <transition enter-active-class="transition ease-out duration-100"
                                    enter-from-class="transform opacity-0 scale-95"
                                    enter-to-class="transform opacity-100 scale-100"
                                    leave-active-class="transition ease-in duration-75"
                                    leave-from-class="transform opacity-100 scale-100"
                                    leave-to-class="transform opacity-0 scale-95">
                                    <div>
                                        <PopoverPanel class="origin-top-right absolute right-0 mt-2 bg-white rounded-md shadow-2xl p-4 ring-1 ring-black ring-opacity-5 focus:outline-none">
                                            <form class="space-y-4">
                                                <div v-for="(option, optionIdx) in section.options"
                                                     :key="option.value" class="flex items-center">
                                                    <input @input="$event => this.handleInput({section, option, checked: $event.target.checked})"
                                                           :id="`filter-${section.id}-${optionIdx}`"
                                                           :name="`${section.id}[]`" :value="option.value"
                                                           type="checkbox" :checked="option.checked"
                                                           class="h-4 w-4 border-gray-300 rounded text-indigo-600 focus:ring-indigo-500"/>
                                                    <label :for="`filter-${section.id}-${optionIdx}`"
                                                           class="ml-3 pr-6 text-sm font-medium text-gray-900 whitespace-nowrap">
                                                        {{ option.label }}
                                                    </label>
                                                </div>
                                            </form>
                                        </PopoverPanel>
                                    </div>
                                </transition>
                            </Popover>
                        </PopoverGroup>
                    </div>
                </div>
            </div>
        </div>

        <!-- Active filters -->
        <div class="bg-gray-100" v-if="this.activeFilters && this.activeFilters.length">
            <div class="max-w-7xl mx-auto py-3 px-4 sm:flex sm:items-center sm:px-6 lg:px-8">
                <h3 class="text-xs font-semibold uppercase tracking-wide text-gray-500">
                    Filters
                    <span class="sr-only">, active</span>
                </h3>

                <div aria-hidden="true" class="hidden w-px h-5 bg-gray-300 sm:block sm:ml-4"/>

                <div class="mt-2 sm:mt-0 sm:ml-4">
                    <div class="-m-1 flex flex-wrap items-center">
                        <span v-for="activeFilter in this.activeFilters" :key="activeFilter.value"
                              class="m-1 inline-flex rounded-full border border-gray-200 items-center py-1.5 pl-3 pr-2 text-sm font-medium bg-white text-gray-900">
                            <span>{{ activeFilter.label }}: {{ activeFilter.valueLabel }}</span>
                            <button type="button" @click="_ => removeFilter(activeFilter)"
                                    class="flex-shrink-0 ml-1 h-4 w-4 p-1 rounded-full inline-flex text-gray-400 hover:bg-gray-200 hover:text-gray-500">
                                <span class="sr-only">Remove filter for {{ activeFilter.label }}</span>
                                <svg class="h-2 w-2" stroke="currentColor" fill="none" viewBox="0 0 8 8">
                                    <path stroke-linecap="round" stroke-width="1.5" d="M1 1l6 6m0-6L1 7"/>
                                </svg>
                            </button>
                        </span>
                    </div>
                </div>
            </div>
        </div>
    </section>
</template>

<script>
import {
    Menu,
    Popover,
    MenuItem,
    MenuItems,
    MenuButton,
    PopoverGroup,
    PopoverPanel,
    PopoverButton,
} from '@headlessui/vue';
import { ChevronDownIcon } from '@heroicons/vue/solid'

export default {
    components: {
        Menu,
        Popover,
        MenuItem,
        MenuItems,
        MenuButton,
        PopoverGroup,
        PopoverPanel,
        PopoverButton,

        // Icons
        ChevronDownIcon
    },
    props: {
        filters: Object,
        sortOptions: Object,
        showMobileFilters: Function,
        // All filters
        activeFilters: Object
    },
    computed: {
        getActiveSortName() {
            let currentSort = this.sortOptions.filter(sortOption => sortOption.current);
            return currentSort.length ? currentSort[0].name : "Sort"
        }
    },
    methods: {
        removeFilter(filter) {
            this.$emit('filter', {
                id: filter.name,
                name: filter.label,
                option: { checked: false, label: filter.valueLabel, value: filter.value }
            })
        },
        handleSort(sort) {
            this.$emit('sort', sort)
        },
        handleInput ({section, option, checked}) {
            const optionClone = Object.assign({}, option);
            optionClone.checked = checked;

            const sectionClone = Object.assign({}, section);
            delete sectionClone.options;

            sectionClone.option = optionClone;
            this.$emit('filter', sectionClone)
        },
        getActiveCount(section) {
            return section.options && section.options && section.options.filter(opt => opt.checked).length || 0
        }
    }
}
</script>
